
import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import "./GridMotion.css";

const GridMotion = ({ items = [], gradientColor = "black" }) => {
  const gridRef = useRef(null);
  const rowRefs = useRef([]);
  const mouseXRef = useRef(window.innerWidth / 2);

  const totalItems = 28;

  const defaultItems = Array.from(
    { length: totalItems },
    (_, index) => `Item ${index + 1}`
  );

  const combinedItems =
    items.length > 0 ? items.slice(0, totalItems) : defaultItems;

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) return; 
    gsap.ticker.lagSmoothing(0);

    const handleMouseMove = (e) => {
      mouseXRef.current = e.clientX;
    };

    const updateMotion = () => {
      const maxMoveAmount = 300;
      const baseDuration = 0.8;
      const inertiaFactors = [0.6, 0.4, 0.3, 0.2];

      rowRefs.current.forEach((row, index) => {
        if (row) {
          const direction = index % 2 === 0 ? 1 : -1;

          const moveAmount =
            ((mouseXRef.current / window.innerWidth) * maxMoveAmount -
              maxMoveAmount / 2) *
            direction;

          gsap.to(row, {
            x: moveAmount,
            duration: baseDuration + inertiaFactors[index % inertiaFactors.length],
            ease: "power3.out",
            overwrite: "auto",
          });
        }
      });
    };

    const removeAnimationLoop = gsap.ticker.add(updateMotion);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      removeAnimationLoop();
    };
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return; 

    const sliderImages = combinedItems.filter((item) => typeof item === "string");
    if (sliderImages.length === 0) return;

    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % sliderImages.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [isMobile, combinedItems]);

  if (isMobile) {
    const sliderImages = combinedItems.filter((item) => typeof item === "string");

    return (
      <div className="grid-background-wrapper">
        <section
          className="grid-motion-mobile-slider"
          style={{
            background: `radial-gradient(circle, ${gradientColor} 0%, transparent 100%)`,
          }}
        >
          {sliderImages.map((src, index) => (
            <img
              key={index}
              src={src}
              alt="grid item"
              className={`grid-motion-mobile-slide${index === activeSlide ? " active" : ""}`}
              loading="lazy"
            />
          ))}
        </section>
      </div>
    );
  }

  return (
    <div className="grid-background-wrapper" ref={gridRef}>
      <section
        className="grid-motion-intro"
        style={{
          background: `radial-gradient(circle, ${gradientColor} 0%, transparent 100%)`,
        }}
      >
        <div className="gridMotion-container">
          {[...Array(4)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              className="grid-motion-row"
              ref={(el) => (rowRefs.current[rowIndex] = el)}
            >
              {[...Array(7)].map((_, itemIndex) => {
                const content = combinedItems[rowIndex * 7 + itemIndex];

                return (
                  <div key={itemIndex} className="grid-motion-item">
                    <div
                      className="grid-motion-item-inner"
                      style={{ backgroundColor: "#111" }}
                    >
                      {typeof content === "string" ? (
                        <img
                          src={content}
                          alt="grid item"
                          className="grid-motion-item-img"
                          loading="lazy"
                        />
                      ) : (
                        <div className="grid-motion-item-content">{content}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="grid-motion-fullview"></div>
      </section>
    </div>
  );
};

export default GridMotion;